/**
 * A Workshop document as blocks (stage 3). The stored markdown is never
 * rewritten to fit a layout: each block keeps the exact range of the source it
 * came from, so the reader can lay a document out as a document (the facts
 * strip, definition rows, numbered sections with an index) and the editor can
 * open one block where it sits and put back exactly the text that was typed.
 *
 * This is a line scanner for the shapes the agents write (front matter,
 * headings, paragraphs, lists, tables, quotes, fenced code), not a markdown
 * renderer: every block still renders through the markdown renderer or a
 * plain reading of its own text.
 */

export type DocFact = { label: string; value: string };

export type DocBlockKind =
  /** Front matter, or a leading run of "Label: value" lines: the facts strip. */
  | "facts"
  /** Front matter that holds more than "key: value" lines: shown as written. */
  | "frontmatter"
  /** H1. */
  | "title"
  /** H2: a numbered section with a place in the index. */
  | "section"
  /** H3. */
  | "subhead"
  /** H4 to H6. */
  | "heading"
  | "paragraph"
  /** One "Label: value" bullet: a definition row. */
  | "definition"
  | "list"
  | "table"
  | "quote"
  | "code"
  | "rule";

export type DocBlock = {
  /** Unique within one reading: the kind and where it starts. */
  key: string;
  kind: DocBlockKind;
  /** The source range, [start, end) in the content. */
  start: number;
  end: number;
  source: string;
  /** A heading's words with the markup taken out. */
  text: string | null;
  facts: DocFact[] | null;
  definition: DocFact | null;
  /** A section's number, from 1. */
  number: number | null;
};

export type ParsedDocument = {
  content: string;
  blocks: DocBlock[];
  /** The H2 blocks, in order: the index. */
  sections: DocBlock[];
  /** Laid out as a document (facts, rows, numbered sections), or plain markdown. */
  structured: boolean;
};

type Line = { text: string; start: number; end: number };

const BLANK = /^\s*$/;
const ATX = /^ {0,3}(#{1,6})(?:[ \t]+(.*?))?(?:[ \t]+#+)?[ \t]*$/;
const FENCE = /^ {0,3}(`{3,}|~{3,})/;
const RULE = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/;
const QUOTE = /^ {0,3}>/;
const LIST_ITEM = /^( {0,3})([-*+]|\d{1,9}[.)])(?:[ \t]+|$)/;
const BULLET_START = /^ {0,3}[-*+][ \t]+\S/;
const ORDERED_START = /^ {0,3}1[.)][ \t]+\S/;
const SETEXT = /^ {0,3}(?:=+|-+)[ \t]*$/;
const TABLE_DELIM = /^ {0,3}\|?[ \t]*:?-+:?[ \t]*(?:\|[ \t]*:?-+:?[ \t]*)*\|?[ \t]*$/;
const FRONT_OPEN = /^---[ \t]*$/;
const FRONT_CLOSE = /^(?:---|\.\.\.)[ \t]*$/;

function splitLines(content: string): Line[] {
  const lines: Line[] = [];
  let at = 0;
  for (;;) {
    const nl = content.indexOf("\n", at);
    let end = nl < 0 ? content.length : nl;
    if (end > at && content[end - 1] === "\r") end -= 1;
    lines.push({ text: content.slice(at, end), start: at, end });
    if (nl < 0) break;
    at = nl + 1;
  }
  return lines;
}

/** A heading's or a label's words: emphasis, code ticks and link targets taken out. */
export function plainInline(text: string): string {
  return text
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/(\*\*|__)(.+?)\1/g, "$2")
    .replace(/(^|[^\w*])[*_]([^*_]+)[*_](?=[^\w*]|$)/g, "$1$2")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

function words(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

/**
 * "**Label:** value", "**Label**: value" or "Label: value" (a short label that
 * starts like a name), else null.
 */
export function readDefinition(text: string): DocFact | null {
  const t = text.trim().replace(/(?: {2,}|\\)$/, "");
  const bold =
    /^(\*\*|__)([^*_]{1,60}?)\1[ \t]*:[ \t]*(\S.*)$/.exec(t) ??
    /^(\*\*|__)([^*_]{1,60}?):\1[ \t]*(\S.*)$/.exec(t);
  if (bold) {
    const label = plainInline(bold[2]);
    return label && words(label) <= 8 ? { label, value: bold[3].trim() } : null;
  }
  const plain = /^([\p{Lu}\p{N}][^:*_`[\]<>|]{0,39}?):[ \t]+(\S.*)$/u.exec(t);
  if (!plain) return null;
  const label = plain[1].trim();
  if (words(label) > 5 || /^(?:https?|mailto)$/i.test(label)) return null;
  return { label, value: plain[2].trim() };
}

/** Front matter's "key: value" lines as facts, or null when it holds more than that. */
function readFrontMatter(lines: string[]): DocFact[] | null {
  const facts: DocFact[] = [];
  for (const line of lines) {
    if (BLANK.test(line)) continue;
    const m = /^([A-Za-z0-9][\w .'&/-]{0,47}):[ \t]*(.*)$/.exec(line);
    if (!m) return null;
    const value = m[2].trim().replace(/^(["'])(.*)\1$/, "$2").trim();
    if (!value) return null;
    facts.push({ label: m[1].replace(/[_-]+/g, " ").trim(), value });
  }
  return facts.length > 0 ? facts : null;
}

function isTableStart(lines: Line[], i: number): boolean {
  const head = lines[i]?.text;
  const delim = lines[i + 1]?.text;
  return (
    !!head &&
    !!delim &&
    head.includes("|") &&
    delim.includes("|") &&
    delim.includes("-") &&
    TABLE_DELIM.test(delim)
  );
}

/** A non-blank line that ends a paragraph or a list: a block of another kind starts. */
function startsBlock(lines: Line[], i: number): boolean {
  const t = lines[i].text;
  return ATX.test(t) || FENCE.test(t) || QUOTE.test(t) || RULE.test(t) || isTableStart(lines, i);
}

function indentOf(text: string): number {
  return text.length - text.trimStart().length;
}

/** The last line from `to` back to `from` that is not blank. */
function lastContentLine(lines: Line[], from: number, to: number): number {
  let last = to;
  while (last > from && BLANK.test(lines[last].text)) last -= 1;
  return last;
}

type Push = (
  kind: DocBlockKind,
  from: number,
  to: number,
  extra?: Partial<Pick<DocBlock, "text" | "facts" | "definition">>,
) => void;

/**
 * A list from line `i`. A bullet list whose every item is one "Label: value"
 * line becomes definition rows, one block each; anything else is one list.
 * Returns the line after it.
 */
function readList(lines: Line[], i: number, push: Push): number {
  const first = LIST_ITEM.exec(lines[i].text);
  if (!first) return i + 1;
  const base = first[1].length;
  const ordered = /\d/.test(first[2]);
  const itemStarts = [i];
  let last = i;
  let j = i + 1;
  while (j < lines.length) {
    const t = lines[j].text;
    if (BLANK.test(t)) {
      let k = j + 1;
      while (k < lines.length && BLANK.test(lines[k].text)) k += 1;
      if (k >= lines.length) break;
      const next = lines[k].text;
      const item = LIST_ITEM.exec(next);
      const sameList = !!item && item[1].length <= base + 1 && /\d/.test(item[2]) === ordered;
      if (sameList || indentOf(next) >= base + 2) {
        j = k;
        continue;
      }
      break;
    }
    const item = LIST_ITEM.exec(t);
    if (item && indentOf(t) <= base + 1) {
      if (/\d/.test(item[2]) !== ordered) break;
      itemStarts.push(j);
    } else if (indentOf(t) < base + 2 && startsBlock(lines, j)) {
      break;
    }
    last = j;
    j += 1;
  }

  const items = itemStarts.map((from, n) => ({
    from,
    to: lastContentLine(lines, from, n + 1 < itemStarts.length ? itemStarts[n + 1] - 1 : last),
  }));
  const definitions = ordered
    ? null
    : items.map(({ from, to }) => {
        const body: string[] = [];
        for (let n = from; n <= to; n += 1) {
          const text = lines[n].text;
          if (n > from && (BLANK.test(text) || LIST_ITEM.test(text.trimStart()))) return null;
          body.push(n === from ? text.replace(LIST_ITEM, "") : text.trim());
        }
        return readDefinition(body.join(" "));
      });
  if (definitions && definitions.every((d): d is DocFact => d !== null)) {
    items.forEach(({ from, to }, n) => push("definition", from, to, { definition: definitions[n] }));
  } else {
    push("list", i, last);
  }
  return last + 1;
}

/** Reads a document into blocks. Every character of `content` outside a block is blank. */
export function parseDocument(content: string): ParsedDocument {
  const lines = splitLines(content);
  const blocks: DocBlock[] = [];
  const push: Push = (kind, from, to, extra = {}) => {
    const start = lines[from].start;
    const end = lines[to].end;
    blocks.push({
      key: `${kind}-${start}`,
      kind,
      start,
      end,
      source: content.slice(start, end),
      text: extra.text ?? null,
      facts: extra.facts ?? null,
      definition: extra.definition ?? null,
      number: null,
    });
  };

  let i = 0;
  if (lines.length > 2 && FRONT_OPEN.test(lines[0].text)) {
    let close = -1;
    for (let n = 1; n < lines.length && close < 0; n += 1) {
      if (FRONT_CLOSE.test(lines[n].text)) close = n;
    }
    if (close > 1) {
      const facts = readFrontMatter(lines.slice(1, close).map((line) => line.text));
      push(facts ? "facts" : "frontmatter", 0, close, { facts });
      i = close + 1;
    }
  }

  while (i < lines.length) {
    const t = lines[i].text;
    if (BLANK.test(t)) {
      i += 1;
      continue;
    }

    const fence = FENCE.exec(t);
    if (fence) {
      const mark = fence[1];
      const close = new RegExp(`^ {0,3}${mark[0] === "`" ? "`" : "~"}{${mark.length},}[ \\t]*$`);
      let j = i + 1;
      while (j < lines.length && !close.test(lines[j].text)) j += 1;
      const to = Math.min(j, lines.length - 1);
      push("code", i, to);
      i = to + 1;
      continue;
    }

    const atx = ATX.exec(t);
    if (atx) {
      const level = atx[1].length;
      const kind: DocBlockKind =
        level === 1 ? "title" : level === 2 ? "section" : level === 3 ? "subhead" : "heading";
      push(kind, i, i, { text: plainInline(atx[2] ?? "") });
      i += 1;
      continue;
    }

    if (RULE.test(t)) {
      push("rule", i, i);
      i += 1;
      continue;
    }

    if (isTableStart(lines, i)) {
      let j = i + 2;
      while (j < lines.length && !BLANK.test(lines[j].text) && lines[j].text.includes("|")) j += 1;
      push("table", i, j - 1);
      i = j;
      continue;
    }

    if (QUOTE.test(t)) {
      let j = i + 1;
      while (j < lines.length && !BLANK.test(lines[j].text) && !ATX.test(lines[j].text) && !FENCE.test(lines[j].text)) {
        j += 1;
      }
      push("quote", i, j - 1);
      i = j;
      continue;
    }

    if (LIST_ITEM.test(t)) {
      i = readList(lines, i, push);
      continue;
    }

    // A paragraph, with its setext underline when it has one.
    let j = i + 1;
    while (j < lines.length) {
      const next = lines[j].text;
      if (BLANK.test(next)) break;
      if (SETEXT.test(next)) {
        j += 1;
        break;
      }
      if (startsBlock(lines, j) || BULLET_START.test(next) || ORDERED_START.test(next)) break;
      j += 1;
    }
    push("paragraph", i, j - 1);
    i = j;
  }

  // A leading run of "Label: value" lines (after the H1 and a lead at most)
  // is the facts strip, when front matter did not already give one.
  if (!blocks.some((block) => block.kind === "facts")) {
    const firstSection = blocks.findIndex((block) => block.kind === "section");
    const limit = Math.min(3, firstSection < 0 ? blocks.length : firstSection);
    for (let n = 0; n < limit; n += 1) {
      const block = blocks[n];
      if (block.kind !== "paragraph") continue;
      const rows = block.source.split("\n");
      const facts = rows.map((row) => readDefinition(row));
      if (rows.length >= 2 && facts.every((fact): fact is DocFact => fact !== null)) {
        blocks[n] = { ...block, kind: "facts", key: `facts-${block.start}`, facts };
        break;
      }
    }
  }

  const sections = blocks.filter((block) => block.kind === "section");
  sections.forEach((block, n) => {
    block.number = n + 1;
  });
  const definitions = blocks.filter((block) => block.kind === "definition").length;
  const structured =
    sections.length >= 2 || blocks.some((block) => block.kind === "facts") || definitions >= 2;
  return { content, blocks, sections, structured };
}

/** "842", "13.7K", "128K". */
export function formatCount(n: number): string {
  if (n < 1000) return String(n);
  const k = n / 1000;
  return `${k >= 100 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, "")}K`;
}

export function replaceRange(content: string, start: number, end: number, text: string): string {
  return content.slice(0, start) + text + content.slice(end);
}

function comparableTitle(text: string): string {
  return plainInline(text)
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

/**
 * The document's own H1 when it only repeats the output's title: the reader
 * shows one title, so this line is not drawn again.
 */
export function repeatedTitle(doc: ParsedDocument, title: string): DocBlock | null {
  const first = doc.blocks.find((block) => block.kind !== "facts" && block.kind !== "frontmatter");
  if (first?.kind !== "title" || !first.text) return null;
  return comparableTitle(first.text) === comparableTitle(title) ? first : null;
}

/** An H1 line carrying a new title, its own "#" kept. */
export function retitledLine(block: DocBlock, title: string): string {
  const mark = /^ {0,3}#[ \t]+/.exec(block.source);
  return `${mark ? mark[0] : "# "}${title}`;
}

/**
 * One passage that changed between two versions: the blocks it replaced and
 * the blocks that replaced them. A passage with nothing added shows where it
 * was, before `anchorKey` (or at the end).
 */
export type DocChange = {
  id: string;
  removed: DocBlock[];
  added: DocBlock[];
  anchorKey: string | null;
  /**
   * The stretch was too long to compare block by block, so it is one change
   * standing for most of the document: the reader says the document was
   * rewritten rather than count passages.
   */
  whole: boolean;
};

/** Past this many comparisons the middle of a long rewrite is one passage. */
const MAX_DIFF_CELLS = 4_000_000;

function signature(block: DocBlock): string {
  return block.source.replace(/\s+/g, " ").trim();
}

type DiffOp =
  | { kind: "same"; a: number; b: number }
  | { kind: "removed"; a: number }
  | { kind: "added"; b: number };

/**
 * The passages that changed from `before` to `after`, block by block: the
 * longest run of blocks the two share stays, and every stretch between is one
 * passage.
 */
export function diffDocuments(
  before: DocBlock[],
  after: DocBlock[],
  maxCells: number = MAX_DIFF_CELLS,
): DocChange[] {
  const a = before.map(signature);
  const b = after.map(signature);
  let head = 0;
  while (head < a.length && head < b.length && a[head] === b[head]) head += 1;
  let endA = a.length;
  let endB = b.length;
  while (endA > head && endB > head && a[endA - 1] === b[endB - 1]) {
    endA -= 1;
    endB -= 1;
  }

  const ops: DiffOp[] = [];
  for (let k = 0; k < head; k += 1) ops.push({ kind: "same", a: k, b: k });
  const n = endA - head;
  const m = endB - head;
  const tooLong = n * m > maxCells;
  if (tooLong) {
    for (let k = head; k < endA; k += 1) ops.push({ kind: "removed", a: k });
    for (let k = head; k < endB; k += 1) ops.push({ kind: "added", b: k });
  } else {
    // lengths[x][y]: the longest shared run of a[head + x..] and b[head + y..].
    const width = m + 1;
    const lengths = new Uint32Array((n + 1) * width);
    for (let x = n - 1; x >= 0; x -= 1) {
      for (let y = m - 1; y >= 0; y -= 1) {
        lengths[x * width + y] =
          a[head + x] === b[head + y]
            ? lengths[(x + 1) * width + y + 1] + 1
            : Math.max(lengths[(x + 1) * width + y], lengths[x * width + y + 1]);
      }
    }
    let x = 0;
    let y = 0;
    while (x < n && y < m) {
      if (a[head + x] === b[head + y]) {
        ops.push({ kind: "same", a: head + x, b: head + y });
        x += 1;
        y += 1;
      } else if (lengths[(x + 1) * width + y] >= lengths[x * width + y + 1]) {
        ops.push({ kind: "removed", a: head + x });
        x += 1;
      } else {
        ops.push({ kind: "added", b: head + y });
        y += 1;
      }
    }
    for (; x < n; x += 1) ops.push({ kind: "removed", a: head + x });
    for (; y < m; y += 1) ops.push({ kind: "added", b: head + y });
  }
  for (let k = 0; k < a.length - endA; k += 1) ops.push({ kind: "same", a: endA + k, b: endB + k });

  const changes: DocChange[] = [];
  let removed: DocBlock[] = [];
  let added: DocBlock[] = [];
  const close = (next: DocBlock | null) => {
    if (removed.length === 0 && added.length === 0) return;
    changes.push({
      id: `change-${changes.length + 1}`,
      removed,
      added,
      anchorKey: added[0]?.key ?? next?.key ?? null,
      whole: tooLong,
    });
    removed = [];
    added = [];
  };
  for (const op of ops) {
    if (op.kind === "same") close(after[op.b]);
    else if (op.kind === "removed") removed.push(before[op.a]);
    else added.push(after[op.b]);
  }
  close(null);
  return changes;
}
